import * as Yup from 'yup';
import { useFormik } from 'formik';

import { EMAIL_INVALID, requiredMsg } from 'lib/constants/messages';

Yup.addMethod(Yup.string, 'requiredIfPeersPresent', function (peerFields, message) {
  return this.test({
    name: 'requiredIfPeersPresent',
    exclusive: false,
    message: message || 'This field is required',
    test(value) {
      const { parent } = this;
      // Check if any of the specified peer fields in the parent object have a truthy value
      const hasAnyPeerField = peerFields.some((field) => Boolean(parent[field]));

      // If a peer field is present, the current field must have a value (value is truthy)
      if (hasAnyPeerField && !value) {
        return false;
      }
      return true;
    }
  });
});

const allFields = ['npi', 'tax_id', 'tax_id_type', 'taxonomy'];
const addressFields = ['address', 'address_2', 'city', 'state', 'zip'];

const CreateUpdateStaffMemberValidationSchema = () =>
  Yup.object().shape({
    f_name: Yup.string().required(requiredMsg('First name')),
    l_name: Yup.string().required(requiredMsg('Last name')),
    role: Yup.number().required(requiredMsg('Role')),
    email: Yup.string().email(EMAIL_INVALID).required(requiredMsg('Email')),
    phone: Yup.string().required(requiredMsg('Phone number')),
    gender: Yup.string().required(requiredMsg('Gender')),
    dob: Yup.date().required(requiredMsg('Date of birth')),
    address_is_same_as_practice: Yup.boolean().default(false),
    taxonomy: Yup.string()
      .length(10, 'Must be exactly 10 characters')
      .matches(/^[a-zA-Z0-9]+$/, 'Must be alphanumeric'),
    npi: Yup.string()
      .length(10, 'NPI must be exactly 10 digits')
      .matches(/^\d{10}$/, 'NPI must be only digits (0-9)')
      .requiredIfPeersPresent(
        allFields.filter((f) => f !== 'npi'),
        requiredMsg('NPI')
      ),
    tax_id: Yup.string()
      .length(9, 'Tax ID must be exactly 9 digits')
      .matches(/^\d{9}$/, 'Tax ID must be only digits (0-9)')
      .requiredIfPeersPresent(
        allFields.filter((f) => f !== 'tax_id'),
        requiredMsg('Tax ID')
      ),
    tax_id_type: Yup.string().requiredIfPeersPresent(
      allFields.filter((f) => f !== 'tax_id_type'),
      requiredMsg('Tax ID type')
    ),
    address: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'address'),
          requiredMsg('Address')
        )
    }),
    city: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'city'),
          requiredMsg('City')
        )
    }),
    state: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema.requiredIfPeersPresent(
          addressFields.filter((f) => f !== 'state'),
          requiredMsg('State')
        )
    }),
    zip: Yup.string().when('address_is_same_as_practice', {
      is: false,
      then: (schema) =>
        schema
          .matches(/^\d{5}(?:-\d{4})?$/, 'Enter a valid zip code (5 digits or 5+4)')
          .requiredIfPeersPresent(
            addressFields.filter((f) => f !== 'zip'),
            requiredMsg('Zip code')
          )
    }),
    local_state_ids: Yup.array().of(Yup.number()).default([]),
    virtual_state_ids: Yup.array().of(Yup.number()).default([])
  });

export default function useNewStaffMemberFormik(staffMember = {}, onSubmit) {
  const formik = useFormik({
    initialValues: {
      f_name: staffMember?.f_name || '',
      m_name: staffMember?.m_name || '',
      l_name: staffMember?.l_name || '',
      gender: staffMember?.gender || null,
      dob: staffMember?.dob || '',
      email: staffMember?.email || '',
      phone: staffMember?.phone || '',
      color: staffMember?.color || '#717680',
      role: staffMember?.role || null,
      is_active: staffMember?.is_active ?? true,
      profession_group_id: staffMember?.profession_group_id || null,
      payment_rate: staffMember?.payment_rate || '0',
      npi: staffMember?.npi || '',
      tax_id: staffMember?.tax_id || '',
      tax_id_type: staffMember?.tax_id_type || '',
      taxonomy: staffMember?.taxonomy || '',
      address: staffMember?.address || '',
      address_2: staffMember?.address_2 || '',
      city: staffMember?.city || '',
      state: staffMember?.state || '',
      zip: staffMember?.zip || '',
      address_is_same_as_practice: false,
      trainings: staffMember?.trainings || [],
      availableTimes: staffMember?.availableTimes || null,
      local_state_ids: Array.isArray(staffMember?.local_state_ids)
        ? staffMember.local_state_ids
        : [],
      virtual_state_ids: Array.isArray(staffMember?.virtual_state_ids)
        ? staffMember.virtual_state_ids
        : []
    },
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: CreateUpdateStaffMemberValidationSchema,
    onSubmit
  });

  return formik;
}
